import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import Checkbox from 'components/checkbox/checkbox';
import { Cookies } from 'enums/types';
import cookieIcon from 'assets/img/cookie.svg';
import close from 'assets/img/subscribe/close-burger-hover.svg';
import * as style from './cookie.module.scss';

type StateChangeHandler = (
	preference: boolean,
	statistical: boolean,
	marketing: boolean,
	isOpenModal: boolean
) => void;

interface Props {
	onStateChange: StateChangeHandler;
	onClose?: (value: boolean) => void;
}

const translations = defineMessages({
	close: {
		id: 'close',
		defaultMessage: 'close',
	},
	cookieIcon: {
		id: 'imageAlt.cookie',
		defaultMessage: 'Vector cookie chip',
	},
});

const SettingsPopup = ({ onStateChange, onClose }: Props) => {
	const { formatMessage } = useIntl();
	const preferenceCookie = localStorage.getItem(Cookies.Preference);
	const statisticalCookie = localStorage.getItem(Cookies.Statistical);
	const marketingCookie = localStorage.getItem(Cookies.Marketing);

	const [preference, setPreference] = useState(preferenceCookie === '1' || false);
	const [statistical, setStatistical] = useState(statisticalCookie === '1' || false);
	const [marketing, setMarketing] = useState(marketingCookie === '1' || false);

	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as HTMLElement)) {
				onClose?.(true);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	const turnOffMainScrollbar = () => {
		const documentIsDefined = typeof document === 'object';
		if (documentIsDefined) {
			const body = document.querySelector('body');
			if (body && !body.classList.contains('overflowHiddenCookie')) {
				body.classList.add('overflowHiddenCookie');
			}
		}
	};

	useEffect(() => {
		if (window.innerHeight < 860 && window.innerWidth < 860) {
			turnOffMainScrollbar();
			window.addEventListener('resize', turnOffMainScrollbar);
			return () => window.removeEventListener('resize', turnOffMainScrollbar);
		}
		return undefined;
	}, []);

	return (
		<div className={style.popupWrapper} ref={wrapperRef}>
			<div onClick={() => onClose && onClose(true)} className={style.close}>
				<img src={close} alt={formatMessage(translations.close)} className={style.closeImg} />
			</div>
			<img src={cookieIcon} alt={formatMessage(translations.cookieIcon)} width={110} />
			<h4 className={style.popupHeading}>
				<FormattedMessage id="cookie.popupTitle" defaultMessage="Bankera cookies explained" />
			</h4>
			<div>
				<Checkbox isCookieBlock checked disabled>
					<FormattedMessage
						id="cookie.necessary"
						defaultMessage="<b>Necessary cookies</b> are essential for the website’s navigation, performance, security and functionality. These cookies are required in order to deliver information and services to you."
						values={{
							b: (...chunks: string[]) => <b>{chunks}</b>,
						}}
					/>
				</Checkbox>
				<Checkbox isCookieBlock checked={preference} onChange={() => setPreference((prevState) => !prevState)}>
					<FormattedMessage
						id="cookie.preference"
						defaultMessage="<b>Preference cookies</b> enable our website to remember the information that alters the way it looks or functions. Rejecting this cookie category might disable some website features."
						values={{
							b: (...chunks: string[]) => <b>{chunks}</b>,
						}}
					/>
				</Checkbox>
				<Checkbox
					isCookieBlock
					checked={statistical}
					onChange={() => setStatistical((prevState) => !prevState)}
				>
					<FormattedMessage
						id="cookie.statistical"
						defaultMessage="<b>Statistical cookies</b> help us understand how you use and interact with the website and allow us to make it better. These cookies are anonymous and provide us generalised data."
						values={{
							b: (...chunks: string[]) => <b>{chunks}</b>,
						}}
					/>
				</Checkbox>
				<Checkbox isCookieBlock checked={marketing} onChange={() => setMarketing((prevState) => !prevState)}>
					<FormattedMessage
						id="cookie.marketing"
						defaultMessage="<b>Marketing cookies</b> are used to deliver targeted content based on your behavioural traits. Additionally, they also help us measure the success of our marketing campaigns."
						values={{
							b: (...chunks: string[]) => <b>{chunks}</b>,
						}}
					/>
				</Checkbox>
			</div>
			<button
				className={classnames(style.button, style.bigButton)}
				onClick={() => onStateChange(preference, statistical, marketing, false)}
			>
				<FormattedMessage id="button.agreeAndSave" defaultMessage="Agree and save" />
			</button>
			<button
				className={classnames(style.link, style.rejectButton)}
				onClick={() => onStateChange(false, false, false, false)}
			>
				<FormattedMessage id="button.rejectAll" defaultMessage="Reject all" />
			</button>
		</div>
	);
};

export default SettingsPopup;
